import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ArrowRight, ArrowLeft } from "lucide-react";
import GlowCard from "../components/GlowCard";
import { WorkArea, WorkPackage } from "./types";

interface PackageHierarchyProps {
  packages: WorkPackage[];
  cwas: WorkArea[];
  onOpen?: (pkg: WorkPackage) => void;
}

interface AreaGroup {
  key: string;
  code: string;
  name?: string;
  sequence?: number;
  cwps: WorkPackage[];
}

const byNo = (a: WorkPackage, b: WorkPackage) => a.packageNo.localeCompare(b.packageNo);
const NO_AREA = "__none__";

/**
 * Work area → CWP → the EWPs and PWPs linked to it.
 *
 * The register says what packages exist; this says how they hang together, which is the first thing
 * an AWP reviewer checks — a CWP with no EWP behind it, or a PWP nothing feeds, is a gap before any
 * document has been read.
 *
 * Links are many-to-many, so one PWP can appear under two CWPs; that is the truth of the job, not a
 * duplicate. EWPs and PWPs on no CWP at all are listed at the bottom rather than hidden, because an
 * unlinked package is exactly the thing that needs seeing.
 */
const PackageHierarchy = ({ packages, cwas, onOpen }: PackageHierarchyProps) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const byId = useMemo(() => new Map(packages.map((p) => [p.id, p])), [packages]);

  const areas = useMemo(() => {
    const known = new Set(cwas.map((a) => a.id));
    const groups: AreaGroup[] = [...cwas]
      .sort(
        (a, b) =>
          (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER) ||
          a.code.localeCompare(b.code),
      )
      .map((a) => ({
        key: a.id,
        code: a.code,
        name: a.name,
        sequence: a.sequence,
        cwps: packages.filter((p) => p.type === "CWP" && p.cwaId === a.id).sort(byNo),
      }));
    const orphanCwps = packages
      .filter((p) => p.type === "CWP" && (!p.cwaId || !known.has(p.cwaId)))
      .sort(byNo);
    if (orphanCwps.length) groups.push({ key: NO_AREA, code: "No work area", cwps: orphanCwps });
    return groups;
  }, [packages, cwas]);

  const unlinked = useMemo(() => {
    const linked = new Set<string>();
    for (const cwp of packages.filter((p) => p.type === "CWP")) {
      for (const id of [...(cwp.ewpIds ?? []), ...(cwp.pwpIds ?? [])]) linked.add(id);
    }
    return packages.filter((p) => p.type !== "CWP" && !linked.has(p.id)).sort(byNo);
  }, [packages]);

  // Which EWPs feed each PWP — the reverse of feedsPwpIds, so a PWP can say what it is waiting on.
  const fedBy = useMemo(() => {
    const out = new Map<string, WorkPackage[]>();
    for (const ewp of packages.filter((p) => p.type === "EWP")) {
      for (const id of ewp.feedsPwpIds ?? []) out.set(id, [...(out.get(id) ?? []), ewp]);
    }
    return out;
  }, [packages]);

  const resolve = (ids?: string[]) =>
    (ids ?? []).map((id) => byId.get(id)).filter((p): p is WorkPackage => !!p).sort(byNo);

  const row = (pkg: WorkPackage, key = pkg.id) => {
    const links =
      pkg.type === "EWP" ? resolve(pkg.feedsPwpIds) : pkg.type === "PWP" ? fedBy.get(pkg.id) ?? [] : [];
    return (
      <div key={key} className="vr-pkg-row">
        <span className={`vr-pkg-type vr-pkg-${pkg.type.toLowerCase()}`}>{pkg.type}</span>
        <button type="button" className="vr-pkg-no" onClick={() => onOpen?.(pkg)} disabled={!onOpen}>
          {pkg.packageNo}
        </button>
        <span className="vr-pkg-title">{pkg.title}</span>
        {links.length > 0 && (
          <span className="vr-pkg-links" title={pkg.type === "EWP" ? "Feeds" : "Fed by"}>
            {pkg.type === "EWP" ? <ArrowRight size={11} /> : <ArrowLeft size={11} />}
            {links.map((l) => l.packageNo).join(", ")}
          </span>
        )}
      </div>
    );
  };

  if (packages.length === 0 && cwas.length === 0) {
    return (
      <GlowCard className="vr-table-card" hover={false}>
        <div className="vr-empty">No work areas or packages yet.</div>
      </GlowCard>
    );
  }

  return (
    <div className="vr-pkg-tree">
      {areas.map((area) => (
        <GlowCard key={area.key} className="vr-pkg-area" hover={false}>
          <div className="vr-pkg-area-head">
            {area.sequence !== undefined && <span className="vr-pkg-seq">Seq {area.sequence}</span>}
            <strong className={area.key === NO_AREA ? "vr-warn" : undefined}>{area.code}</strong>
            {area.name && <span className="vr-cell-dim">{area.name}</span>}
          </div>

          {area.cwps.length === 0 && <div className="vr-pkg-gap">No CWPs in this work area yet</div>}

          {area.cwps.map((cwp) => {
            const open = !collapsed[cwp.id];
            const ewps = resolve(cwp.ewpIds);
            const pwps = resolve(cwp.pwpIds);
            return (
              <div key={cwp.id} className="vr-pkg-cwp-block">
                <div className="vr-pkg-cwp-head">
                  <button
                    type="button"
                    className="vr-nav-caret"
                    aria-label={open ? `Collapse ${cwp.packageNo}` : `Expand ${cwp.packageNo}`}
                    aria-expanded={open}
                    onClick={() => setCollapsed((c) => ({ ...c, [cwp.id]: open }))}
                  >
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  {row(cwp)}
                </div>
                {open && (
                  <div className="vr-pkg-children">
                    <div>
                      <div className="vr-pkg-col-head">Engineering (EWPs)</div>
                      {ewps.length ? (
                        ewps.map((e) => row(e, `${cwp.id}:${e.id}`))
                      ) : (
                        <div className="vr-pkg-gap">No EWP linked</div>
                      )}
                    </div>
                    <div>
                      <div className="vr-pkg-col-head">Procurement (PWPs)</div>
                      {pwps.length ? (
                        pwps.map((p) => row(p, `${cwp.id}:${p.id}`))
                      ) : (
                        <div className="vr-pkg-gap">No PWP linked</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </GlowCard>
      ))}

      {unlinked.length > 0 && (
        <GlowCard className="vr-pkg-area" hover={false}>
          <div className="vr-pkg-area-head">
            <strong className="vr-warn">Not linked to a CWP</strong>
            <span className="vr-cell-dim">{unlinked.length}</span>
          </div>
          <div className="vr-pkg-loose">{unlinked.map((p) => row(p))}</div>
        </GlowCard>
      )}
    </div>
  );
};

export default PackageHierarchy;
